import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Check, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, eur, km, dateFr, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const empty = { vehicle_id: "", type_entretien: "", due_date: "", due_km: "", cout_estime: 0, commentaire: "" };

const TYPES_SUGGERES = ["Vidange", "Filtre à huile", "Filtre à air", "Filtre à carburant",
  "Filtre d'habitacle", "Bougies d'allumage", "Liquide de frein", "Plaquettes de frein",
  "Disques de frein", "Courroie de distribution", "Courroie accessoire",
  "Liquide de refroidissement", "Pneus", "Batterie", "Contrôle technique",
  "Révision constructeur", "Climatisation", "Boîte de vitesses (huile)", "Amortisseurs"];

export default function CalendarPage() {
  const [vehicles, setVehicles] = useState([]);
  const [echeances, setEcheances] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const load = () => api.get("/echeances").then((r) => setEcheances(r.data));

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.due_date && !form.due_km) return toast.error("Indiquez une date ou un kilométrage");
    setSaving(true);
    const payload = {
      ...form,
      due_date: form.due_date || null,
      due_km: form.due_km ? Number(form.due_km) : null,
      cout_estime: Number(form.cout_estime) || 0,
    };
    try {
      if (editId) {
        await api.put(`/echeances/${editId}`, payload);
        toast.success("Échéance modifiée");
      } else {
        await api.post("/echeances", payload);
        toast.success("Échéance ajoutée");
      }
      setForm(empty);
      setEditId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    setEditId(null);
    setForm(empty);
    setSuggestion(null);
    setOpen(true);
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setSuggestion(null);
    setForm({
      vehicle_id: e.vehicle_id,
      type_entretien: e.type_entretien,
      due_date: (e.due_date || "").slice(0, 10),
      due_km: e.due_km || "",
      cout_estime: e.cout_estime || 0,
      commentaire: e.commentaire || "",
    });
    setOpen(true);
  };

  const fetchSuggestion = async (type_entretien, vehicle_id) => {
    if (!type_entretien || !vehicle_id) return setSuggestion(null);
    try {
      const { data } = await api.get("/periodicites", {
        params: { vehicle_id, type_entretien },
      });
      setSuggestion(data.suggestion);
    } catch {
      setSuggestion(null);
    }
  };

  const applySuggestion = () => {
    const veh = vehicles.find((v) => v.id === form.vehicle_id);
    const next = { ...form };
    if (suggestion.intervalle_km && veh) {
      next.due_km = (veh.kilometrage || 0) + suggestion.intervalle_km;
    }
    if (suggestion.intervalle_mois) {
      const d = new Date();
      d.setMonth(d.getMonth() + suggestion.intervalle_mois);
      next.due_date = d.toISOString().slice(0, 10);
    }
    setForm(next);
    toast.success("Suggestion appliquée, vous pouvez l'ajuster");
  };

  const toggle = async (id) => {
    await api.patch(`/echeances/${id}`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/echeances/${id}`);
    toast.success("Échéance supprimée");
    load();
  };

  const pending = echeances.filter((e) => !e.done);
  const done = echeances.filter((e) => e.done);

  const groups = pending.reduce((acc, e) => {
    const key = e.due_date
      ? new Date(e.due_date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
      : "Basé sur le kilométrage";
    (acc[key] = acc[key] || []).push(e);
    return acc;
  }, {});

  const Row = ({ e }) => (
    <div data-testid={`echeance-item-${e.id}`}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className={`font-display text-lg font-semibold ${e.done ? "text-slate-400 line-through" : "text-slate-900"}`}>
            {e.type_entretien}
          </h3>
          <span data-testid={`echeance-source-${e.id}`}
            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
              e.source === "suivi"
                ? "text-blue-700 bg-blue-50 border-blue-200"
                : "text-slate-600 bg-slate-100 border-slate-200"
            }`}>
            {e.source === "suivi" ? "Périodique" : "Ponctuelle"}
          </span>
        </div>
        <p className="text-sm text-slate-500">{e.vehicule}</p>
        <p className="mt-2 text-sm text-slate-600">
          {e.due_date ? dateFr(e.due_date) : ""}{e.due_date && e.due_km ? " · " : ""}
          {e.due_km ? `à ${km(e.due_km)}` : ""}
          {e.cout_estime ? ` · estimé ${eur(e.cout_estime)}` : ""}
          {e.source === "suivi" && e.terme_atteint
            ? ` · ${e.terme_atteint === "km" ? "seuil kilométrique" : "seuil temporel"} atteint en premier`
            : ""}
        </p>
        {e.commentaire && <p className="mt-1 text-sm text-slate-500">{e.commentaire}</p>}
      </div>
      <div className="flex flex-col items-end gap-3 shrink-0">
        {!e.done && <StatusBadge status={e.statut} />}
        {e.source === "suivi" ? (
          <Link to={`/app/vehicules/${e.vehicle_id}`} data-testid={`echeance-open-vehicle-${e.id}`}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            Modifier
          </Link>
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={() => startEdit(e)} data-testid={`echeance-edit-${e.id}`}
              className="text-slate-400 hover:text-blue-600 transition-colors" title="Modifier">
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={() => toggle(e.id)} data-testid={`echeance-toggle-${e.id}`}
              className="text-slate-400 hover:text-emerald-600 transition-colors" title="Marquer comme fait">
              <Check className="h-4 w-4" />
            </button>
            <button onClick={() => remove(e.id)} data-testid={`echeance-delete-${e.id}`}
              className="text-slate-400 hover:text-red-600 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="calendar-page" className="space-y-8">
      <div className="flex items-end justify-between gap-4 animate-fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Échéances</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Calendrier
          </h1>
        </div>
        <Button onClick={startCreate} data-testid="open-echeance-form-btn"
          className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Échéance
        </Button>
      </div>

      {pending.length === 0 && done.length === 0 ? (
        <div data-testid="calendar-empty" className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <CalendarDays className="h-8 w-8 text-slate-400 mx-auto" />
          <p className="mt-4 font-display text-lg font-semibold text-slate-800">Aucune échéance</p>
          <p className="mt-1 text-sm text-slate-500">
            Saisissez vos échéances ponctuelles ici, ou vos intervalles dans « État de mon
            entretien » : les deux apparaissent dans ce calendrier.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {Object.entries(groups).map(([label, items]) => (
            <div key={label}>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">{label}</h2>
              <div className="space-y-4">{items.map((e) => <Row key={e.id} e={e} />)}</div>
            </div>
          ))}
          {done.length > 0 && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Terminées</h2>
              <div className="space-y-4">{done.map((e) => <Row key={e.id} e={e} />)}</div>
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="echeance-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editId ? "Modifier l'échéance" : "Nouvelle échéance"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="echeance-form">
            <div className="space-y-2">
              <Label>Véhicule</Label>
              <select required value={form.vehicle_id}
                onChange={(ev) => {
                  setForm({ ...form, vehicle_id: ev.target.value });
                  fetchSuggestion(form.type_entretien, ev.target.value);
                }}
                data-testid="echeance-vehicle-select"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                <option value="">Choisir…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.marque} {v.modele}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Type d'entretien</Label>
              <Input required value={form.type_entretien} list="types-entretien"
                onChange={(ev) => {
                  setForm({ ...form, type_entretien: ev.target.value });
                  fetchSuggestion(ev.target.value, form.vehicle_id);
                }}
                data-testid="echeance-type-input" placeholder="Vidange" />
              <datalist id="types-entretien">
                {TYPES_SUGGERES.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
            {suggestion && (
              <div data-testid="echeance-suggestion"
                className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-700">
                  Périodicité couramment observée :{" "}
                  <span className="font-semibold">
                    {[suggestion.intervalle_km && `tous les ${suggestion.intervalle_km.toLocaleString("fr-FR")} km`,
                      suggestion.intervalle_mois && `tous les ${suggestion.intervalle_mois} mois`]
                      .filter(Boolean).join(" ou ")}
                  </span>
                  <span className="block text-xs text-slate-500">
                    Suggestion indicative, à vérifier dans votre carnet constructeur.
                  </span>
                </p>
                <Button type="button" onClick={applySuggestion} data-testid="echeance-apply-suggestion-btn"
                  className="rounded-full bg-blue-600 hover:bg-blue-700 shrink-0">
                  Appliquer
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date prévue</Label>
                <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="echeance-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Kilométrage prévu</Label>
                <Input type="number" value={form.due_km} onChange={set("due_km")} data-testid="echeance-km-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Coût estimé (€)</Label>
              <Input type="number" step="0.01" value={form.cout_estime} onChange={set("cout_estime")} data-testid="echeance-cout-input" />
            </div>
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Textarea value={form.commentaire} onChange={set("commentaire")} rows={2} data-testid="echeance-comment-input" />
            </div>
            <Button type="submit" disabled={saving} data-testid="echeance-submit-btn"
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement…" : editId ? "Enregistrer les modifications" : "Enregistrer l'échéance"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
